/**
 * One recording, one clock, one element.
 *
 * ── Nothing loads until somebody presses play ───────────────────────────────
 * Opening a transcript is reading, not listening. The `HTMLAudioElement` is created on the first
 * press and not before, and the presigned URL is minted in the same moment — a signature handed
 * out on the render would be a live credential for every transcript anybody merely opened.
 *
 * ── One recording plays app-wide ───────────────────────────────────────────
 * The registry below is module-level rather than per dialog, because two recordings sounding at
 * once is never what anybody meant: starting one pauses the other wherever it is.
 *
 * ── The clock is optimistic, and that is what stops the bar jumping back ────
 * `currentTime` is the position the *reader* asked for, not the position the element has reached.
 * A seek moves it immediately and sets `pendingSeek`; while that is set the element's own
 * `timeupdate` is ignored, so a scrubber released at 41:00 does not snap back to 12:03 for the
 * two seconds the network takes. `seeked` clears it and the element's clock takes over again.
 *
 * ── The latest command wins, even over a signature still in flight ──────────
 * The position and the play/pause a reader wants are held in refs that every command writes, and
 * signing reads neither until the URL lands: what is then applied is the *latest* pair — the
 * element is put at the wanted position and plays only if sound is still wanted, so a Pause or a
 * jump made while the URL is being minted is what happens. A command during signing never mints a
 * second URL; it only moves those refs. Each load carries a generation, so a completion overtaken
 * by a newer load, by a changed recording or by a released element does nothing at all.
 *
 * ── What is remembered, and for how long ───────────────────────────────────
 * The **speed** belongs to the person: it is how fast they listen, so it is stored per user in
 * `localStorage` and every recording opens at it. The **position** belongs to the session: a map
 * in this module, keyed by artifact, so reopening a recording a minute later resumes where it was
 * and a reload starts at 0:00 rather than resuming a meeting somebody finished with yesterday.
 *
 * ── Two objects, and only one of them is ever a fallback ───────────────────
 * The derived mono Opus is what plays: it is a tenth the size and it is exactly the audio the
 * words were transcribed from. A browser that cannot decode it says so through `canPlayType`, and
 * then the *class of the upload* decides — because falling back is only sound when the fallback is
 * itself sound. An **audio** upload is signed instead: its bytes are the same audio in a container
 * the browser does understand. A **video** upload is not, ever: streaming two gigabytes of mp4 to
 * hear the talking in it is not a fallback, it is a different product. Such a recording is
 * `unavailable` from the first paint, with `unavailableReason: "unsupported_format"`, and nothing
 * is signed at all — the bar stays in place saying what would fix it.
 */

import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useObservedAction } from "@/hooks/use-observed-convex";
import { resolveDocumentRefusalMessage } from "../lib/document-processing-view";
import {
  clampPlaybackTime,
  coercePlaybackRate,
  DEFAULT_PLAYBACK_RATE,
  nextTurnBoundary,
  type PlayableTurn,
  type PlaybackRate,
} from "../lib/transcript-playback";

/** Where the speed a person listens at is kept, for every recording they open. */
export const PLAYBACK_RATE_STORAGE_KEY = "df:transcript-playback-rate";

/** The media type the derived audio is, asked of the browser before anything is signed. */
const DERIVED_AUDIO_MIME_TYPE = "audio/ogg; codecs=opus";

/** How far the element's clock may already be from a requested position for the seek to be free. */
const SEEK_TOLERANCE_SECONDS = 0.05;

export type TranscriptPlayerStatus =
  | "idle"
  | "paused"
  | "playing"
  | "buffering"
  | "ended"
  | "unavailable";

/**
 * Why there is nothing to play, when there is nothing to play.
 *
 * Two reasons rather than one, because they are two different sentences to a person: a browser
 * that cannot decode the audio has a fix (a newer browser), and a media failure has none the
 * reader can act on beyond trying again.
 */
export type TranscriptPlayerUnavailableReason = "unsupported_format" | "playback_error" | null;

export type TranscriptPlayer = {
  status: TranscriptPlayerStatus;
  /** Set exactly while `status` is `"unavailable"`, and `null` otherwise. */
  unavailableReason: TranscriptPlayerUnavailableReason;
  /** The position the reader is at — the requested one while a seek is still buffering. */
  currentTime: number;
  /** The recording's length as the element reports it, or the length the transcript knew. */
  duration: number;
  /** The position a seek is travelling to, `null` once the element has arrived. */
  pendingSeek: number | null;
  rate: PlaybackRate;
  /** A drag is in progress: the highlight follows, the reading column does not. */
  isScrubbing: boolean;
  /**
   * Bumped by every *explicit* seek, whoever asked for it. The reading column watches it to know
   * that following should re-engage — a person who jumped somewhere is asking to be taken there.
   */
  seekNonce: number;
  play: (at?: number) => void;
  pause: () => void;
  toggle: () => void;
  seek: (seconds: number, options?: { play?: boolean }) => void;
  setRate: (rate: number) => void;
  beginScrub: () => void;
  endScrub: () => void;
  jumpToTurnBoundary: (direction: "forward" | "back") => void;
};

export type UseTranscriptPlayerOptions = {
  projectId: Id<"projects">;
  /** The recording being read. `null`, or `enabled: false`, stops and releases everything. */
  artifactId: string | null;
  /**
   * The transcription on screen, so the sound is the audio these words were transcribed from.
   * `null` before its first page has landed, which signs the document's current audio.
   */
  processingRevisionId: string | null;
  /** `playableMedia` from the transcript query — the one switch, never a file extension. */
  enabled: boolean;
  /** The recording's length where the transcript already states it, before any metadata loads. */
  durationSeconds: number | null;
  /**
   * What the *upload* is, from the shared classifier the row menu and the upload ceilings use.
   * It decides whether there is a fallback at all when the browser cannot decode the derived
   * audio: an audio upload is one, a video upload is not.
   */
  originalMediaClass: "audio" | "video" | null;
  /** The turns the boundary jumps step through, in spoken order. */
  turns: readonly PlayableTurn[];
};

/**
 * The one player currently making sound, as a function that stops it.
 *
 * A single slot rather than a set: what has to be true is "at most one", and a set would let a
 * missed cleanup accumulate pausers for elements that are long gone.
 */
let soundingPlayer: (() => void) | null = null;

/** Where each recording was left, for as long as this tab holds this module. */
const rememberedPositions = new Map<string, number>();

/**
 * Whether this browser can decode the audio the lane produces.
 *
 * Asked once and cached: the answer is a property of the browser, not of a recording, and it has
 * to be known *before* the first press so a recording that cannot be played says so on the first
 * paint rather than after somebody has pressed play. The throwaway element loads nothing — it is
 * never given a source.
 */
let opusPlayable: boolean | null = null;

function canPlayDerivedAudio(): boolean {
  if (opusPlayable !== null) return opusPlayable;
  try {
    opusPlayable = document.createElement("audio").canPlayType(DERIVED_AUDIO_MIME_TYPE) !== "";
  } catch {
    // A runtime with no media element at all is not a runtime that can contradict this.
    opusPlayable = true;
  }
  return opusPlayable;
}

/**
 * Forget which recording is sounding and where every recording was left.
 *
 * Everything this module holds outside React — the sounding slot, the cached codec answer and the
 * per-recording positions — cleared together. Nothing in the app calls it: a tab's whole life is
 * one session, and a test running several players in one module instance needs the fresh start a
 * reload gives a person.
 */
export function resetTranscriptPlayerSession(): void {
  soundingPlayer = null;
  opusPlayable = null;
  rememberedPositions.clear();
}

function readStoredRate(): PlaybackRate {
  try {
    const stored = window.localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY);
    return stored === null ? DEFAULT_PLAYBACK_RATE : coercePlaybackRate(stored);
  } catch {
    // A browser with storage switched off listens at the default and forgets nothing.
    return DEFAULT_PLAYBACK_RATE;
  }
}

function writeStoredRate(rate: PlaybackRate): void {
  try {
    window.localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(rate));
  } catch {
    // Nothing depends on the write; the speed still applies to this element.
  }
}

export function useTranscriptPlayer(options: UseTranscriptPlayerOptions): TranscriptPlayer {
  const { projectId, artifactId, enabled, durationSeconds, originalMediaClass, turns } = options;

  // Nothing here would fall back to: the browser cannot decode the derived audio and the upload is
  // a video, whose bytes are not an audio fallback at any size worth streaming.
  const unsupportedFormat = enabled && !canPlayDerivedAudio() && originalMediaClass !== "audio";

  const createPlaybackUrl = useObservedAction(api.artifactMedia.createMediaPlaybackUrl, {
    feature: "documents",
    operation: "transcript-playback",
    includeRequestId: true,
  });

  const [status, setStatus] = useState<TranscriptPlayerStatus>(
    unsupportedFormat ? "unavailable" : "idle"
  );
  const [unavailableReason, setUnavailableReason] = useState<TranscriptPlayerUnavailableReason>(
    unsupportedFormat ? "unsupported_format" : null
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);
  const [rate, setRateState] = useState<PlaybackRate>(readStoredRate);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [seekNonce, setSeekNonce] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const statusRef = useRef<TranscriptPlayerStatus>(unsupportedFormat ? "unavailable" : "idle");
  const unsupportedRef = useRef(unsupportedFormat);
  unsupportedRef.current = unsupportedFormat;
  /**
   * Where the reader wants to be. Every command writes it and the element's own clock follows it
   * while sound comes out, so a load that lands later starts here rather than wherever the press
   * that began it was.
   */
  const positionRef = useRef(0);
  /**
   * Whether the reader wants sound. Independent of anything in flight: a signature being minted
   * consults it only when it lands, and any pause — this reader's, or another recording claiming
   * the sound — clears it.
   */
  const desiredPlayingRef = useRef(false);
  const durationRef = useRef(0);
  const rateRef = useRef<PlaybackRate>(rate);
  const pendingSeekRef = useRef<number | null>(null);
  /** When the signature the element is playing on lapses, or `null` while nothing is loaded. */
  const signatureExpiryRef = useRef<number | null>(null);
  const hasSourceRef = useRef(false);
  const remintedRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const artifactRef = useRef<string | null>(enabled ? artifactId : null);
  /** Kept in a ref so a page landing mid-session does not rebuild the load callback. */
  const revisionRef = useRef<string | null>(options.processingRevisionId);
  const turnsRef = useRef<readonly PlayableTurn[]>(turns);
  /** The load in flight, so two presses in the same frame mint one signature. */
  const loadingRef = useRef(false);
  /**
   * Which load a completion belongs to. A newer load, a changed recording and a released element
   * all move it on, and a signature that lands under an old number is dropped unapplied.
   */
  const loadGenerationRef = useRef(0);
  /** Breaks the cycle between the element's error handler and the load that repairs it. */
  const loadRef = useRef<(() => void) | null>(null);

  turnsRef.current = turns;
  revisionRef.current = options.processingRevisionId;
  rateRef.current = rate;

  const applyStatus = useCallback(
    (next: TranscriptPlayerStatus, reason: TranscriptPlayerUnavailableReason = null) => {
      statusRef.current = next;
      setStatus(next);
      // The reason belongs to the state it explains and is dropped with it, so nothing can read a
      // stale explanation off a player that is playing again.
      setUnavailableReason(next === "unavailable" ? reason : null);
    },
    []
  );

  const applyPosition = useCallback((seconds: number) => {
    positionRef.current = seconds;
    setCurrentTime(seconds);
    const artifact = artifactRef.current;
    if (artifact !== null) rememberedPositions.set(artifact, seconds);
  }, []);

  const applyPendingSeek = useCallback((seconds: number | null) => {
    pendingSeekRef.current = seconds;
    setPendingSeek(seconds);
  }, []);

  const stopFrames = useCallback(() => {
    if (frameRef.current === null) return;
    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  // `timeupdate` fires about four times a second, which is a playhead that visibly steps. The
  // frame loop runs only while sound is coming out, and cancels itself the moment it stops.
  const startFrames = useCallback(() => {
    if (frameRef.current !== null) return;
    const tick = () => {
      const audio = audioRef.current;
      if (audio === null || statusRef.current !== "playing") {
        frameRef.current = null;
        return;
      }
      if (pendingSeekRef.current === null) applyPosition(audio.currentTime);
      frameRef.current = window.requestAnimationFrame(tick);
    };
    frameRef.current = window.requestAnimationFrame(tick);
  }, [applyPosition]);

  const pauseElement = useCallback(() => {
    // Whoever asked — this reader, or another recording taking the sound — nothing is waiting on a
    // signature to start playing any more.
    desiredPlayingRef.current = false;
    stopFrames();
    const audio = audioRef.current;
    if (audio !== null) audio.pause();
    if (statusRef.current === "playing" || statusRef.current === "buffering") {
      applyStatus("paused");
    }
  }, [applyStatus, stopFrames]);

  // The identity the app-wide registry holds. The ref keeps the registered function stable while
  // the body it calls is re-created on every render.
  const pauseRef = useRef(pauseElement);
  pauseRef.current = pauseElement;
  const registryEntry = useRef(() => {
    pauseRef.current();
  }).current;

  const releaseSound = useCallback(() => {
    if (soundingPlayer === registryEntry) soundingPlayer = null;
  }, [registryEntry]);

  const claimSound = useCallback(() => {
    if (soundingPlayer !== null && soundingPlayer !== registryEntry) soundingPlayer();
    soundingPlayer = registryEntry;
  }, [registryEntry]);

  /**
   * A media error, told apart by one question: had the signature already lapsed?
   *
   * A six-hour signature outlived by a longer reading session is the one failure that repairs
   * itself, and it repairs itself **once** — a URL that fails again on a fresh signature is a
   * failure of something else, and re-minting in a loop would hammer the action. Anything else
   * keeps `currentTime` exactly where it was, so pressing play again retries from where the
   * reader is rather than restarting the recording.
   */
  const handleElementError = useCallback(() => {
    const expiry = signatureExpiryRef.current;
    const lapsed = expiry !== null && Date.now() >= expiry;
    if (lapsed && !remintedRef.current) {
      remintedRef.current = true;
      // The kept position and the kept intent are already the desired pair; the fresh signature
      // resumes on them.
      loadRef.current?.();
      return;
    }
    // Nothing will sound from here without another press, so nothing is waiting for sound.
    desiredPlayingRef.current = false;
    stopFrames();
    releaseSound();
    applyPendingSeek(null);
    applyStatus("unavailable", "playback_error");
  }, [applyPendingSeek, applyStatus, releaseSound, stopFrames]);

  /** The element, created on the first press and wired once. */
  const ensureAudio = useCallback((): HTMLAudioElement => {
    const existing = audioRef.current;
    if (existing !== null) return existing;
    const audio = document.createElement("audio");
    audio.preload = "auto";
    audio.playbackRate = rateRef.current;
    // Pitch correction is the difference between listening at 1.5× and listening to chipmunks.
    audio.preservesPitch = true;
    audio.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        durationRef.current = audio.duration;
        setDuration(audio.duration);
      }
    });
    audio.addEventListener("timeupdate", () => {
      if (pendingSeekRef.current !== null) return;
      applyPosition(audio.currentTime);
    });
    audio.addEventListener("seeked", () => {
      applyPendingSeek(null);
      applyPosition(audio.currentTime);
    });
    audio.addEventListener("waiting", () => {
      if (statusRef.current === "playing") applyStatus("buffering");
    });
    audio.addEventListener("playing", () => {
      // A signature that worked is one worth re-minting from again if it later lapses.
      remintedRef.current = false;
      applyStatus("playing");
      startFrames();
    });
    audio.addEventListener("pause", () => {
      if (statusRef.current === "ended" || statusRef.current === "unavailable") return;
      stopFrames();
      applyStatus("paused");
    });
    audio.addEventListener("ended", () => {
      // The recording asked for has been heard; a jump from here is a jump, not a resumption.
      desiredPlayingRef.current = false;
      stopFrames();
      releaseSound();
      // The readout stays at the total: the recording is over, not back at the beginning.
      applyPosition(durationRef.current > 0 ? durationRef.current : positionRef.current);
      applyStatus("ended");
    });
    audio.addEventListener("error", handleElementError);
    audioRef.current = audio;
    return audio;
  }, [
    applyPendingSeek,
    applyPosition,
    applyStatus,
    handleElementError,
    releaseSound,
    startFrames,
    stopFrames,
  ]);

  /**
   * Put a signature on the element and take it to wherever the reader by then wants to be.
   *
   * Nothing about the request is decided at the press: the position and the play state are read
   * when the URL lands, so the last command before it is the one that happens. A call made while a
   * signature is already in flight moves the bar to the new position and mints nothing — one press
   * is one signature, however many commands follow it.
   *
   * The position is applied before `play()` rather than after, so a press on a turn forty minutes
   * in never plays a second of the opening first.
   */
  const load = useCallback(async (): Promise<void> => {
    const artifact = artifactRef.current;
    if (artifact === null) return;
    applyStatus("buffering");
    applyPendingSeek(positionRef.current);
    if (loadingRef.current) return;
    const audio = ensureAudio();
    loadingRef.current = true;
    const generation = ++loadGenerationRef.current;
    try {
      // Safari before 18.4 cannot decode Opus in Ogg. Reaching here without it means the upload
      // is itself audio — a video with no decodable derived audio never gets this far, because
      // `play` refuses it and nothing is signed.
      const source = canPlayDerivedAudio() ? "derived_audio" : "original";
      const revision = revisionRef.current;
      const result = await createPlaybackUrl({
        projectId,
        artifactId: artifact as Id<"artifacts">,
        source,
        // The audio the words on screen were transcribed from, not whatever the document reads
        // as now — a viewer pinned to an older transcription plays that run's own sound.
        ...(revision === null
          ? {}
          : { processingRevisionId: revision as Id<"artifactProcessingRevisions"> }),
      });
      if (loadGenerationRef.current !== generation) return;
      if (audioRef.current !== audio || artifactRef.current !== artifact) return;
      signatureExpiryRef.current = result.expiresAt;
      hasSourceRef.current = true;
      audio.src = result.url;
      audio.playbackRate = rateRef.current;
      audio.preservesPitch = true;
      const at = clampPlaybackTime(positionRef.current, durationRef.current);
      audio.currentTime = at;
      applyPosition(at);
      if (!desiredPlayingRef.current) {
        applyPendingSeek(null);
        applyStatus("paused");
        return;
      }
      claimSound();
      await audio.play();
    } catch (error) {
      if (loadGenerationRef.current !== generation || audioRef.current !== audio) return;
      stopFrames();
      releaseSound();
      applyPendingSeek(null);
      applyStatus("unavailable", "playback_error");
      toast.error(resolveDocumentRefusalMessage(error, "Couldn't play this recording"));
    } finally {
      // A load the release or a newer press has already superseded owns nothing to clear.
      if (loadGenerationRef.current === generation) loadingRef.current = false;
    }
  }, [
    applyPendingSeek,
    applyPosition,
    applyStatus,
    claimSound,
    createPlaybackUrl,
    ensureAudio,
    projectId,
    releaseSound,
    stopFrames,
  ]);

  loadRef.current = () => {
    void load();
  };

  const play = useCallback(
    (at?: number) => {
      if (!enabled || unsupportedRef.current || artifactRef.current === null) return;
      // A finished recording restarts: there is nothing left to resume.
      const from = at ?? (statusRef.current === "ended" ? 0 : positionRef.current);
      const target = clampPlaybackTime(from, durationRef.current);
      applyPosition(target);
      desiredPlayingRef.current = true;
      // The sound is claimed by the press, not by the signature that follows it: a recording still
      // being signed has to lose the sound to a press elsewhere, and lose it now rather than when
      // its URL arrives.
      claimSound();
      const audio = audioRef.current;
      if (audio === null || !hasSourceRef.current) {
        void load();
        return;
      }
      if (Math.abs(audio.currentTime - target) > SEEK_TOLERANCE_SECONDS) {
        applyPendingSeek(target);
        audio.currentTime = target;
      }
      applyStatus("buffering");
      void Promise.resolve(audio.play()).catch(() => {
        // A refused `play()` is reported by the element's own `error` event where it is a media
        // failure; anything else (an autoplay policy, a stale element) leaves the bar paused.
        if (statusRef.current === "buffering") applyStatus("paused");
      });
    },
    [applyPendingSeek, applyPosition, applyStatus, claimSound, enabled, load]
  );

  const pause = useCallback(() => {
    releaseSound();
    pauseElement();
  }, [pauseElement, releaseSound]);

  const toggle = useCallback(() => {
    if (statusRef.current === "playing" || statusRef.current === "buffering") {
      pause();
      return;
    }
    play();
  }, [pause, play]);

  const seek = useCallback(
    (seconds: number, seekOptions: { play?: boolean } = {}) => {
      if (!enabled || unsupportedRef.current || artifactRef.current === null) return;
      const target = clampPlaybackTime(seconds, durationRef.current);
      applyPosition(target);
      if (seekOptions.play) {
        desiredPlayingRef.current = true;
        claimSound();
      } else if (!hasSourceRef.current) {
        // A seek is not a press: where no sound is coming out yet it moves the highlight and asks
        // for none, which is also the answer a signature still in flight lands on.
        desiredPlayingRef.current = false;
      }
      setSeekNonce((nonce) => nonce + 1);
      const audio = audioRef.current;
      if (audio === null || !hasSourceRef.current) {
        // Nothing is loaded: the highlight has moved, and only a press that also asks to play
        // pays for a signature. One already in flight travels to this position instead.
        if (seekOptions.play) void load();
        else if (loadingRef.current) applyPendingSeek(target);
        return;
      }
      applyPendingSeek(target);
      audio.currentTime = target;
      if (seekOptions.play) {
        applyStatus("buffering");
        void Promise.resolve(audio.play()).catch(() => {
          if (statusRef.current === "buffering") applyStatus("paused");
        });
        return;
      }
      if (statusRef.current === "ended") applyStatus("paused");
    },
    [applyPendingSeek, applyPosition, applyStatus, claimSound, enabled, load]
  );

  const setRate = useCallback((next: number) => {
    const coerced = coercePlaybackRate(next);
    rateRef.current = coerced;
    setRateState(coerced);
    writeStoredRate(coerced);
    const audio = audioRef.current;
    if (audio !== null) {
      audio.playbackRate = coerced;
      audio.preservesPitch = true;
    }
  }, []);

  // A drag is silent: scrubbing through speech at pointer speed is noise, and the element seeks
  // on every move.
  const beginScrub = useCallback(() => {
    setIsScrubbing(true);
    const audio = audioRef.current;
    if (audio !== null) audio.muted = true;
  }, []);

  const endScrub = useCallback(() => {
    setIsScrubbing(false);
    const audio = audioRef.current;
    if (audio !== null) audio.muted = false;
  }, []);

  const jumpToTurnBoundary = useCallback(
    (direction: "forward" | "back") => {
      const target = nextTurnBoundary(turnsRef.current, positionRef.current, direction);
      if (target === null) return;
      // A boundary jump keeps whatever the reader last asked for, including through a signature
      // still being minted — where the status says "buffering" and the intent is what decides.
      seek(target, { play: desiredPlayingRef.current });
    },
    [seek]
  );

  // The recording changed, or playback was switched off: stop, release the element, and take back
  // the position this recording was last left at, so reopening it resumes rather than restarts.
  // The dialog unmounts when it closes, which is what stops playback on Close and on Escape.
  useEffect(() => {
    const nextArtifact = enabled ? artifactId : null;
    artifactRef.current = nextArtifact;
    const remembered = nextArtifact === null ? 0 : (rememberedPositions.get(nextArtifact) ?? 0);
    positionRef.current = remembered;
    setCurrentTime(remembered);
    setIsScrubbing(false);
    // Another recording's measured length must never bound this one's seeks; the effect below
    // puts back whatever the transcript states about the new one.
    durationRef.current = 0;
    setDuration(0);
    if (unsupportedFormat) applyStatus("unavailable", "unsupported_format");
    return () => {
      stopFrames();
      releaseSound();
      const audio = audioRef.current;
      audioRef.current = null;
      hasSourceRef.current = false;
      signatureExpiryRef.current = null;
      remintedRef.current = false;
      loadingRef.current = false;
      // A signature still being minted for the recording being let go belongs to nothing now.
      loadGenerationRef.current += 1;
      desiredPlayingRef.current = false;
      if (audio !== null) {
        audio.pause();
        // Detaching the source is what actually stops the download; a paused element with a
        // `src` goes on buffering a five-hour recording nobody is listening to.
        audio.removeAttribute("src");
        audio.load();
      }
      pendingSeekRef.current = null;
      setPendingSeek(null);
      statusRef.current = "idle";
      setStatus("idle");
    };
  }, [applyStatus, artifactId, enabled, releaseSound, stopFrames, unsupportedFormat]);

  // The transcript states the recording's length before any element has loaded metadata, and it
  // is what the total reads and what a seek is clamped against until then. The element's own
  // measurement, once it has one, is the better number and is never overwritten by this.
  useEffect(() => {
    const known =
      typeof durationSeconds === "number" && Number.isFinite(durationSeconds) && durationSeconds > 0
        ? durationSeconds
        : 0;
    if (known === 0 || durationRef.current > 0) return;
    durationRef.current = known;
    setDuration(known);
  }, [durationSeconds]);

  // The format answer is known on the render, and the commands already refuse on it in that same
  // render (`unsupportedRef`). Reported through the state alone it would arrive a commit later,
  // once the recording-change effect had written it, and a surface deciding on the render that
  // enables the player — a citation landing as the first page arrives — would read "idle".
  return useMemo(
    () => ({
      status: unsupportedFormat ? "unavailable" : status,
      unavailableReason: unsupportedFormat ? "unsupported_format" : unavailableReason,
      currentTime,
      duration,
      pendingSeek,
      rate,
      isScrubbing,
      seekNonce,
      play,
      pause,
      toggle,
      seek,
      setRate,
      beginScrub,
      endScrub,
      jumpToTurnBoundary,
    }),
    [
      beginScrub,
      currentTime,
      duration,
      endScrub,
      isScrubbing,
      jumpToTurnBoundary,
      pause,
      pendingSeek,
      play,
      rate,
      seek,
      seekNonce,
      setRate,
      status,
      toggle,
      unavailableReason,
      unsupportedFormat,
    ]
  );
}
